import req from 'shared/req';
import {queryParamsToString} from 'shared/utils/url-helpers';
import JWTService from './jwt-service';
import reqWithErrorResponseTransforming from 'src/utils/error-middlewear/req-with-error-transforming';
import {identity} from 'lodash';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ItemValue = Record<string, any>;

interface RequestOptions {
  url: string;
  method?: string;
  data?: ItemValue;
}

interface CRUDServiceOptions<T> {
  baseUrl: string;
  useJWT?: boolean;
  token?: string | null;
  wrapErrors?: boolean;
  apiMapper?: (item: T) => T;
}

/*
 * Provide a baseUrl and optionally a type to create a service that can perform all of the normal CRUD requests
 * based on normal RESTful patterns.
 *
 * Example usage:
 *
 * export default SomeCoolResourceService = new CRUDService('/v1/some-cool-resources');
 *
 * Can now use it like SomeCoolResourceService.index((resources) => setResources(resources));
 *
 * TODO: apiMapper is used when we couldn't update the backend API (because we can't upgrade the iOS app yet). We should remove every reference to apiMapper and do that logic on the backend once we are able to alter API shapes again.
 */
export default class CRUDService<T = ItemValue> {
  BASE_URL: string;
  USE_JWT: boolean;
  TOKEN: string | null = null;
  WRAP_ERRORS = false;
  apiMapper: (item: T) => T;

  constructor(options: string | CRUDServiceOptions<T>) {
    if (typeof options === 'string') {
      options = {
        baseUrl: options,
      };
    }

    this.BASE_URL = options.baseUrl;
    this.USE_JWT = options.useJWT || false;
    this.TOKEN = options.token || null;
    this.WRAP_ERRORS = options.wrapErrors || false;
    this.apiMapper = options.apiMapper || identity;
  }

  async index(query = {}): Promise<T[]> {
    const response = await this.request<T[]>({url: this.BASE_URL, data: query});
    return response.map(this.apiMapper);
  }

  async show(id, queryParams = {}, url: string | null = null): Promise<T> {
    const response = await this.request<T>({
      url: this.createFullUrl(url || this.persistedUrl(id), queryParams),
    });
    return this.apiMapper(response);
  }

  async create(data, queryParams = {}, url: string | null = null): Promise<T> {
    const response = await this.request<T>({
      url: this.createFullUrl(url || this.BASE_URL, queryParams),
      method: 'post',
      data,
    });
    return this.apiMapper(response);
  }

  async update(id, data, queryParams = {}): Promise<T> {
    const response = await this.request<T>({
      url: this.createFullUrl(this.persistedUrl(id), queryParams),
      method: 'patch',
      data,
    });
    return this.apiMapper(response);
  }

  createOrUpdate(data, queryParams = {}): Promise<T> {
    const id = data._id || data.id;

    if (id) {
      return this.update(id, data, queryParams);
    } else {
      return this.create(data, queryParams);
    }
  }

  destroy(id, queryParams = {}): Promise<Record<string, never>> {
    return this.request<Record<string, never>>({
      url: this.createFullUrl(this.persistedUrl(id), queryParams),
      method: 'delete',
    });
  }

  async custom<Type = T>(
    path: string,
    queryParams = {},
    method = 'get',
    data: ItemValue | undefined = undefined
  ): Promise<Type> {
    return this.request<Type>({
      url: this.createFullUrl(this.persistedUrl(path), queryParams),
      method,
      data,
    });
  }

  persistedUrl(id): string {
    return `${this.BASE_URL}${id !== null ? `/${id}` : ''}`;
  }

  createFullUrl(url, queryParams = {}): string {
    if (!Object.keys(queryParams).length) {
      return url;
    }

    return `${url}?${queryParamsToString(queryParams)}`;
  }

  async request<Type>(options: RequestOptions): Promise<Type> {
    const headers: Record<string, string> = {};

    if (this.USE_JWT) {
      headers.Authorization = `Bearer ${await JWTService.token()}`;
    }

    if (this.TOKEN) {
      headers.Authorization = `Bearer ${this.TOKEN}`;
    }

    const requestFunction = this.WRAP_ERRORS ? reqWithErrorResponseTransforming : req;

    return requestFunction<Type>({
      ...options,
      headers,
    });
  }
}
